// Zustand store, organized as game / analysis / chat / ui slices in one
// create() call. All server mutations live here; components stay declarative.

import { create } from 'zustand';
import type {
  AnalysisResponse,
  ChatMessage,
  Color,
  GameDetail,
  GameStatus,
  GradeResponse,
  HealthResponse,
  MoveRow,
  PlayMoveResponse,
  ScoreBreakdown,
  ScoreEstimateResponse,
  Vertex,
} from '../../shared/types.ts';
import { isBoardVertex, isPass, isResign, ptToVertex, vertexToPt } from '../../shared/go/coords.ts';
import { applyMove, checkMove } from '../../shared/go/rules.ts';
import { opposite, type GameRecord } from '../../shared/go/game.ts';
import { markDeadFromOwnership, toggleDeadGroup } from '../../shared/go/scoring.ts';
import { api, ApiError, errorMessage, isAborted, isWarming, streamChat } from '../api.ts';
import { describeMove, recordFromDetail, replayAt } from '../derive.ts';

const CONFIRM_19_KEY = 'learn-baduk:confirm19';

function loadConfirm19(): boolean {
  try {
    const raw = localStorage.getItem(CONFIRM_19_KEY);
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}

export interface FadingCapture {
  vertex: Vertex;
  color: Color;
  key: number;
}

export type AnalysisStatus = 'idle' | 'loading' | 'warming' | 'error';

interface Store {
  // ----- game slice -----
  gameId: number | null;
  reviewMode: boolean;
  detail: GameDetail | null;
  record: GameRecord | null;
  /** Move-list cursor; null = following the latest move. */
  cursor: number | null;
  playPending: boolean;
  botThinking: boolean;
  lastError: string | null;
  announcement: string;
  fadingCaptures: FadingCapture[];
  // scoring marks (local, editable; pushed to the server on finalize)
  deadStones: Vertex[];
  forcedDame: Vertex[];
  /** True once the user has toggled any dead/dame mark — the ownership seed must not overwrite. */
  marksDirty: boolean;
  scoringSeededFor: number | null;
  scoreLoading: boolean;

  loadGame(id: number, review: boolean): Promise<void>;
  refresh(): Promise<void>;
  play(vertex: Vertex): Promise<void>;
  maybeBotMove(): Promise<void>;
  undo(): Promise<void>;
  resume(): Promise<void>;
  seedScoring(): Promise<void>;
  finalize(): Promise<ScoreBreakdown | null>;
  toggleDead(vertex: Vertex): void;
  toggleDame(vertex: Vertex): void;
  setCursor(cursor: number | null): void;
  dismissError(): void;

  // ----- analysis slice -----
  analysis: AnalysisResponse | null;
  analysisStatus: AnalysisStatus;
  analysisFor: number | null;
  scoreEstimate: ScoreEstimateResponse | null;
  scoreEstimateAt: number | null;
  lastGrade: GradeResponse | null;
  gradeLoading: boolean;

  fetchAnalysis(turn: number): Promise<void>;
  fetchScoreEstimate(): Promise<void>;
  fetchGrade(moveNumber: number): Promise<GradeResponse | null>;

  // ----- chat slice -----
  chatMessages: ChatMessage[];
  streamText: string;
  streamTool: string | null;
  chatBusy: boolean;
  chatError: string | null;

  sendChat(message: string): Promise<void>;

  // ----- ui slice -----
  health: HealthResponse | null;
  confirm19: boolean;
  pendingConfirm: Vertex | null;
  /** True when the server gate answered 401 — the passphrase screen shows. */
  authRequired: boolean;

  fetchHealth(): Promise<void>;
  setConfirm19(on: boolean): void;
  setPendingConfirm(vertex: Vertex | null): void;
  /** Resolves to null on success, or a message to show next to the input. */
  submitPassphrase(passphrase: string): Promise<string | null>;
}

let fadeKey = 0;
let fadeTimer: ReturnType<typeof setTimeout> | null = null;

export const useStore = create<Store>()((set, get) => {
  // Abort plumbing: one controller per chat stream, one shared controller for
  // the analysis/score/grade fetches of the current game session. Both are
  // torn down in loadGame so navigation cancels server-side work.
  let chatCtrl: AbortController | null = null;
  let chatDone: Promise<void> | null = null;
  let sessionCtrl: AbortController | null = null;

  /** Adopt a fresh server GameDetail, preserving in-flight local state. */
  function adoptDetail(detail: GameDetail, opts: { adoptMarks: boolean }) {
    const patch: Partial<Store> = {
      detail,
      record: recordFromDetail(detail),
    };
    // Never let a background refresh overwrite marks the user has toggled
    // (marksDirty); only a deliberate adopt (load/resume) resets them.
    if (opts.adoptMarks || (get().scoringSeededFor !== detail.game.id && !get().marksDirty)) {
      patch.deadStones = detail.deadStones;
      patch.forcedDame = detail.forcedDame;
      if (opts.adoptMarks) patch.marksDirty = false;
    }
    if (!get().chatBusy) {
      // Keep locally-recovered messages (negative ids) that the server does
      // not know about; drop any that have since been persisted verbatim.
      const persisted = new Set(detail.chat.map((m) => `${m.role}\n${m.content}`));
      const localTail = get().chatMessages.filter(
        (m) => m.id < 0 && !persisted.has(`${m.role}\n${m.content}`),
      );
      patch.chatMessages = localTail.length > 0 ? [...detail.chat, ...localTail] : detail.chat;
    }
    set(patch);
  }

  /** Merge a PlayMoveResponse into the current detail (replacing any optimistic row). */
  function applyPlayResponse(resp: PlayMoveResponse) {
    const detail = get().detail;
    if (!detail) return;
    const moves = detail.moves.slice();
    const idx = moves.findIndex((m) => m.moveNumber === resp.move.moveNumber);
    if (idx >= 0) moves[idx] = resp.move;
    else moves.push(resp.move);
    const next: GameDetail = {
      ...detail,
      moves,
      game: { ...detail.game, status: resp.status, moveCount: moves.length },
      toPlay: resp.toPlay,
      botToMove: resp.botToMove,
      epoch: resp.epoch,
      consecutivePasses: resp.consecutivePasses,
      captures: resp.captures,
    };
    const record = recordFromDetail(next);
    if (resp.status === 'finished' && next.game.result === null) {
      // The play response carries no result/winner; a move can only finish a
      // game by resignation, which the shared replay derives ('B+R' / 'W+R').
      const end = replayAt(record);
      if (end.result !== null) {
        next.game = { ...next.game, result: end.result, winner: end.winner };
      }
    }
    set({ detail: next, record });
  }

  function setFading(captured: Vertex[], color: Color) {
    if (captured.length === 0) return;
    const fading = captured.map((vertex) => ({ vertex, color, key: fadeKey++ }));
    set({ fadingCaptures: fading });
    if (fadeTimer) clearTimeout(fadeTimer);
    fadeTimer = setTimeout(() => set({ fadingCaptures: [] }), 320);
  }

  return {
    // ----- game slice -----
    gameId: null,
    reviewMode: false,
    detail: null,
    record: null,
    cursor: null,
    playPending: false,
    botThinking: false,
    lastError: null,
    announcement: '',
    fadingCaptures: [],
    deadStones: [],
    forcedDame: [],
    marksDirty: false,
    scoringSeededFor: null,
    scoreLoading: false,

    async loadGame(id, review) {
      // Tear down the previous session: cancel analysis/score/grade fetches
      // and abort (then await) any chat stream so its handlers cannot write
      // into the freshly loaded game — even when re-entering the same game.
      sessionCtrl?.abort();
      sessionCtrl = new AbortController();
      if (chatCtrl) {
        const prev = chatDone;
        chatCtrl.abort();
        chatCtrl = null;
        await prev?.catch(() => {});
      }
      set({
        gameId: id,
        reviewMode: review,
        detail: null,
        record: null,
        cursor: null,
        playPending: false,
        botThinking: false,
        lastError: null,
        announcement: '',
        fadingCaptures: [],
        deadStones: [],
        forcedDame: [],
        marksDirty: false,
        scoringSeededFor: null,
        scoreLoading: false,
        analysis: null,
        analysisStatus: 'idle',
        analysisFor: null,
        scoreEstimate: null,
        scoreEstimateAt: null,
        lastGrade: null,
        gradeLoading: false,
        chatMessages: [],
        streamText: '',
        streamTool: null,
        chatBusy: false,
        chatError: null,
        pendingConfirm: null,
      });
      try {
        const detail = await api.getGame(id);
        if (get().gameId !== id) return;
        adoptDetail(detail, { adoptMarks: true });
      } catch (err) {
        if (get().gameId !== id) return;
        set({ lastError: errorMessage(err) });
      }
    },

    async refresh() {
      const { gameId, detail, playPending, botThinking } = get();
      if (gameId === null || playPending || botThinking) return;
      // Snapshot what the store looked like when this fetch was issued: if a
      // play/bot-move/undo lands (or starts) while the GET is in flight, the
      // response describes an older position and must be discarded, or it
      // would clobber confirmed moves and corrupt the record.
      const movesBefore = detail?.moves.length ?? -1;
      const epochBefore = detail?.epoch ?? -1;
      try {
        const fetched = await api.getGame(gameId);
        const now = get();
        if (now.gameId !== gameId) return;
        if (now.playPending || now.botThinking) return; // mutation in flight
        const cur = now.detail;
        if (cur && (cur.moves.length !== movesBefore || cur.epoch !== epochBefore)) return;
        adoptDetail(fetched, { adoptMarks: false });
      } catch {
        /* transient refresh failures are silent */
      }
    },

    async play(vertex) {
      const { detail, record, playPending, botThinking, reviewMode } = get();
      if (!detail || !record || playPending || botThinking || reviewMode) return;
      if (detail.game.status !== 'active') return;
      const gameId = detail.game.id;
      const color = detail.toPlay;
      const size = detail.game.boardSize;

      let captured: Vertex[] = [];
      if (isBoardVertex(vertex)) {
        const latest = replayAt(record);
        const pt = vertexToPt(vertex, size);
        const reason = checkMove(latest.position, color, pt);
        if (reason !== null) {
          set({ lastError: `That point is not playable (${reason.replace('_', ' ')}).` });
          return;
        }
        captured = applyMove(latest.position, color, pt).captured.map((p) => ptToVertex(p, size));
      }

      // Optimistic apply via the shared engine.
      const optimistic: MoveRow = {
        moveNumber: detail.moves.length + 1,
        color,
        vertex,
        captured,
        grade: null,
        winrateBefore: null,
        winrateAfter: null,
        scoreLeadAfter: null,
      };
      const passes = isPass(vertex) ? detail.consecutivePasses + 1 : 0;
      const status: GameStatus = isResign(vertex)
        ? 'finished'
        : passes >= 2
          ? 'scoring'
          : detail.game.status;
      const moves = [...detail.moves, optimistic];
      const next: GameDetail = {
        ...detail,
        moves,
        game: { ...detail.game, status, moveCount: moves.length },
        toPlay: opposite(color),
        consecutivePasses: passes,
        captures: {
          B: detail.captures.B + (color === 'B' ? captured.length : 0),
          W: detail.captures.W + (color === 'W' ? captured.length : 0),
        },
      };
      set({
        detail: next,
        record: recordFromDetail(next),
        playPending: true,
        pendingConfirm: null,
        cursor: null,
        lastError: null,
        announcement: describeMove(color, vertex, captured.length),
      });
      setFading(captured, opposite(color));

      try {
        const resp = await api.playMove(gameId, vertex);
        if (get().gameId !== gameId) return;
        applyPlayResponse(resp);
        set({ playPending: false });
        if (resp.botToMove) void get().maybeBotMove();
      } catch (err) {
        if (get().gameId !== gameId) return;
        set({ playPending: false, lastError: errorMessage(err) });
        await get().refresh(); // roll back to server truth
      }
    },

    async maybeBotMove() {
      const { detail, botThinking, reviewMode } = get();
      if (!detail || botThinking || reviewMode) return;
      if (!detail.botToMove || detail.game.status !== 'active') return;
      const gameId = detail.game.id;
      const epoch = detail.epoch;
      set({ botThinking: true, announcement: 'GNU Go is thinking' });
      try {
        const resp = await api.botMove(gameId, detail.moves.length + 1);
        const now = get();
        if (now.gameId !== gameId || !now.detail) return;
        if (now.detail.epoch !== epoch) return; // undo landed mid-think; discard
        applyPlayResponse(resp);
        set({
          botThinking: false,
          announcement: describeMove(resp.move.color, resp.move.vertex, resp.move.captured.length),
        });
        setFading(resp.move.captured, opposite(resp.move.color));
        if (resp.botToMove) void get().maybeBotMove();
      } catch (err) {
        if (get().gameId !== gameId) return;
        set({ botThinking: false });
        // 409 = stale expectation (e.g. undo raced the request): resync silently.
        if (!(err instanceof Error && 'status' in err && (err as { status: number }).status === 409)) {
          set({ lastError: errorMessage(err) });
        }
        await get().refresh();
      } finally {
        if (get().gameId === gameId && get().botThinking) set({ botThinking: false });
      }
    },

    async undo() {
      const { detail, playPending, reviewMode } = get();
      if (!detail || playPending || reviewMode) return;
      const gameId = detail.game.id;
      // Freeze input for the whole undo round-trip: until the refetched
      // detail is adopted, the local record is stale and a quick click would
      // play a move numbered against the pre-undo state.
      set({ playPending: true });
      let botToMove = false;
      try {
        const resp = await api.undo(gameId);
        if (get().gameId !== gameId) return;
        set({
          cursor: null,
          botThinking: false,
          pendingConfirm: null,
          announcement: `Undid ${resp.removedPlies} ${resp.removedPlies === 1 ? 'move' : 'moves'}`,
        });
        const fresh = await api.getGame(gameId);
        if (get().gameId !== gameId) return;
        adoptDetail(fresh, { adoptMarks: false });
        botToMove = resp.botToMove;
      } catch (err) {
        if (get().gameId !== gameId) return;
        set({ lastError: errorMessage(err) });
      } finally {
        if (get().gameId === gameId && get().playPending) set({ playPending: false });
      }
      if (botToMove && get().gameId === gameId) void get().maybeBotMove();
    },

    async resume() {
      const { detail } = get();
      if (!detail) return;
      const gameId = detail.game.id;
      try {
        const fresh = await api.resume(gameId);
        if (get().gameId !== gameId) return;
        set({
          scoringSeededFor: null,
          scoreEstimate: null,
          scoreEstimateAt: null,
          deadStones: [],
          forcedDame: [],
          marksDirty: false,
          announcement: 'Play resumed',
        });
        if (get().analysisStatus === 'warming') set({ analysisStatus: 'idle' });
        adoptDetail(fresh, { adoptMarks: true });
        if (fresh.botToMove) void get().maybeBotMove();
      } catch (err) {
        if (get().gameId !== gameId) return;
        set({ lastError: errorMessage(err) });
      }
    },

    async seedScoring() {
      const { detail, record, scoreLoading } = get();
      if (!detail || !record || scoreLoading) return;
      const gameId = detail.game.id;
      if (get().scoringSeededFor === gameId) return;
      const at = detail.moves.length; // position the ownership query analyzes
      set({ scoringSeededFor: gameId, scoreLoading: true });
      try {
        const est = await api.score(gameId, sessionCtrl?.signal);
        const now = get();
        if (now.gameId !== gameId || !now.record) return;
        const position = replayAt(now.record).position;
        // The scoring board stays interactive while the ownership query runs:
        // seed from KataGo only if the user has not touched the (local,
        // editable) marks in the meantime — never overwrite their toggles.
        let dead = now.deadStones;
        let dame = now.forcedDame;
        if (!now.marksDirty && dead.length === 0 && dame.length === 0) {
          const seed = markDeadFromOwnership(position, est.ownership);
          dead = seed.deadStones;
          dame = seed.forcedDame;
        }
        set({
          scoreEstimate: est,
          scoreEstimateAt: at,
          deadStones: dead,
          forcedDame: dame,
          analysisStatus: 'idle',
        });
      } catch (err) {
        if (isAborted(err) || get().gameId !== gameId) return;
        if (isWarming(err)) {
          set({ scoringSeededFor: null, analysisStatus: 'warming' });
        } else {
          set({ scoringSeededFor: null, lastError: errorMessage(err) });
        }
      } finally {
        if (get().gameId === gameId) set({ scoreLoading: false });
      }
    },

    async finalize() {
      const { detail, deadStones, forcedDame } = get();
      if (!detail) return null;
      const gameId = detail.game.id;
      try {
        const breakdown = await api.finalize(gameId, { deadStones, forcedDame });
        if (get().gameId !== gameId) return breakdown;
        const cur = get().detail;
        if (cur) {
          set({
            detail: {
              ...cur,
              scoreBreakdown: breakdown,
              game: {
                ...cur.game,
                status: 'finished',
                result: breakdown.result,
                winner: breakdown.winner,
              },
            },
            announcement: `Game finished: ${breakdown.result}`,
          });
        }
        return breakdown;
      } catch (err) {
        if (get().gameId === gameId) set({ lastError: errorMessage(err) });
        return null;
      }
    },

    toggleDead(vertex) {
      const { record, deadStones } = get();
      if (!record) return;
      const position = replayAt(record).position;
      set({ deadStones: toggleDeadGroup(position, deadStones, vertex), marksDirty: true });
    },

    toggleDame(vertex) {
      const { forcedDame } = get();
      set({
        forcedDame: forcedDame.includes(vertex)
          ? forcedDame.filter((v) => v !== vertex)
          : [...forcedDame, vertex],
        marksDirty: true,
      });
    },

    setCursor(cursor) {
      const { detail } = get();
      const max = detail?.moves.length ?? 0;
      const clamped = cursor === null ? null : Math.max(0, Math.min(max, cursor));
      set({ cursor: clamped === max ? null : clamped, pendingConfirm: null });
    },

    dismissError() {
      set({ lastError: null });
    },

    // ----- analysis slice -----
    analysis: null,
    analysisStatus: 'idle',
    analysisFor: null,
    scoreEstimate: null,
    scoreEstimateAt: null,
    lastGrade: null,
    gradeLoading: false,

    async fetchAnalysis(turn) {
      const { detail, analysisStatus } = get();
      if (!detail || analysisStatus === 'loading') return;
      const gameId = detail.game.id;
      set({ analysisStatus: 'loading', analysisFor: turn });
      try {
        const analysis = await api.analysis(gameId, { turn, topN: 6 }, sessionCtrl?.signal);
        if (get().gameId !== gameId) return;
        set({ analysis, analysisStatus: 'idle' });
      } catch (err) {
        if (isAborted(err) || get().gameId !== gameId) return;
        set({ analysisStatus: isWarming(err) ? 'warming' : 'error' });
        if (!isWarming(err)) set({ lastError: errorMessage(err) });
      }
    },

    async fetchScoreEstimate() {
      const { detail, scoreLoading } = get();
      if (!detail || scoreLoading) return;
      const gameId = detail.game.id;
      // Stamp the estimate with the move count the request was issued at, not
      // the count when the response lands — a bot reply mid-flight must not
      // relabel a stale ownership snapshot as fresh.
      const at = detail.moves.length;
      set({ scoreLoading: true });
      try {
        const est = await api.score(gameId, sessionCtrl?.signal);
        if (get().gameId !== gameId) return;
        set({ scoreEstimate: est, scoreEstimateAt: at });
        if (get().analysisStatus === 'warming') set({ analysisStatus: 'idle' });
      } catch (err) {
        if (isAborted(err) || get().gameId !== gameId) return;
        if (isWarming(err)) set({ analysisStatus: 'warming' });
        else set({ lastError: errorMessage(err) });
      } finally {
        if (get().gameId === gameId) set({ scoreLoading: false });
      }
    },

    async fetchGrade(moveNumber) {
      const { detail, gradeLoading } = get();
      if (!detail || gradeLoading) return null;
      const gameId = detail.game.id;
      set({ gradeLoading: true });
      try {
        const grade = await api.grade(gameId, moveNumber, sessionCtrl?.signal);
        if (get().gameId !== gameId) return grade;
        const cur = get().detail;
        if (cur) {
          const moves = cur.moves.map((m) =>
            m.moveNumber === moveNumber
              ? {
                  ...m,
                  grade: grade.grade,
                  winrateBefore: grade.winrateBefore,
                  winrateAfter: grade.winrateAfter,
                }
              : m,
          );
          set({ detail: { ...cur, moves }, lastGrade: grade });
        }
        return grade;
      } catch (err) {
        if (!isAborted(err) && get().gameId === gameId && !isWarming(err)) {
          set({ lastError: errorMessage(err) });
        }
        return null;
      } finally {
        if (get().gameId === gameId) set({ gradeLoading: false });
      }
    },

    // ----- chat slice -----
    chatMessages: [],
    streamText: '',
    streamTool: null,
    chatBusy: false,
    chatError: null,

    async sendChat(message) {
      const { detail, chatBusy, cursor } = get();
      const trimmed = message.trim();
      if (!detail || chatBusy || !trimmed) return;
      const gameId = detail.game.id;
      // One controller per stream, doubling as the request token: loadGame
      // aborts it and clears chatCtrl, which turns every handler below into a
      // no-op — a remount of the SAME game cannot be written into by a stale
      // stream (the gameId alone would not distinguish the two sessions).
      const ctrl = new AbortController();
      chatCtrl = ctrl;
      const current = () => chatCtrl === ctrl && get().gameId === gameId;
      const localUser: ChatMessage = {
        id: -Date.now(),
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      };
      set({
        chatMessages: [...get().chatMessages, localUser],
        chatBusy: true,
        streamText: '',
        streamTool: null,
        chatError: null,
      });
      const body: { message: string; cursor?: number } = { message: trimmed };
      if (cursor !== null && cursor < detail.moves.length) body.cursor = cursor;

      const finalizeLocally = () => {
        const text = get().streamText;
        if (!text) return;
        const localAssistant: ChatMessage = {
          id: -Date.now() - 1,
          role: 'assistant',
          content: text,
          createdAt: new Date().toISOString(),
        };
        set({ chatMessages: [...get().chatMessages, localAssistant] });
      };

      const work = (async () => {
        try {
          await streamChat(
            gameId,
            body,
            {
              onDelta: (text) => {
                if (!current()) return;
                set({ streamText: get().streamText + text, streamTool: null });
              },
              onTool: (name) => {
                if (!current()) return;
                set({ streamTool: name });
              },
              onError: (msg) => {
                if (!current()) return;
                set({ chatError: msg });
              },
            },
            ctrl.signal,
          );
          if (!current()) return;
          try {
            const history = await api.chatHistory(gameId, ctrl.signal);
            if (!current()) return;
            const last = history.messages[history.messages.length - 1];
            if (get().streamText && (!last || last.role !== 'assistant')) {
              // server did not persist the reply; keep the local transcript
              set({ chatMessages: history.messages });
              finalizeLocally();
            } else {
              set({ chatMessages: history.messages });
            }
          } catch (err) {
            if (isAborted(err) || !current()) return;
            finalizeLocally();
          }
        } catch (err) {
          if (isAborted(err) || !current()) return;
          set({ chatError: errorMessage(err) });
          finalizeLocally();
        } finally {
          if (current()) {
            set({ chatBusy: false, streamText: '', streamTool: null });
          }
          if (chatCtrl === ctrl) chatCtrl = null;
        }
      })();
      chatDone = work;
      await work;
    },

    // ----- ui slice -----
    health: null,
    confirm19: loadConfirm19(),
    pendingConfirm: null,
    authRequired: false,

    async fetchHealth() {
      // Only store a new object when a field actually changed — a fresh
      // identity every poll re-renders every health subscriber (and busts the
      // stone-layer memo on 19x19 boards) for no reason.
      const apply = (health: HealthResponse) => {
        const cur = get().health;
        if (
          cur &&
          cur.gnugo === health.gnugo &&
          cur.katago === health.katago &&
          cur.claude === health.claude
        ) {
          return;
        }
        set({ health });
      };
      try {
        apply(await api.health());
      } catch (err) {
        // The gate answers 401 auth_required on every route; the recurring
        // health poll is where the client notices it (both at boot and if the
        // cookie ever disappears mid-session).
        if (err instanceof ApiError && err.code === 'auth_required') {
          set({ authRequired: true });
          return;
        }
        apply({ gnugo: false, katago: 'down', claude: false });
      }
    },

    async submitPassphrase(passphrase) {
      try {
        await api.auth(passphrase);
      } catch (err) {
        return errorMessage(err);
      }
      set({ authRequired: false });
      void get().fetchHealth();
      return null;
    },

    setConfirm19(on) {
      try {
        localStorage.setItem(CONFIRM_19_KEY, on ? '1' : '0');
      } catch {
        /* private mode */
      }
      set({ confirm19: on, pendingConfirm: null });
    },

    setPendingConfirm(vertex) {
      set({ pendingConfirm: vertex });
    },
  };
});

/**
 * Move numbers of played board moves that still expect a grade. The server
 * only background-grades human moves in bot games, so anything else returns []
 * — otherwise the grade poll would refetch forever waiting on grades that can
 * never arrive.
 */
export function ungradedMoveNumbers(detail: GameDetail): number[] {
  const human = detail.game.humanColor;
  if (detail.game.mode !== 'bot' || human === null) return [];
  return detail.moves
    .filter((m) => isBoardVertex(m.vertex) && m.grade === null && m.color === human)
    .map((m) => m.moveNumber);
}

/** The most recent human board move (for "How was my last move?"). */
export function lastHumanBoardMove(detail: GameDetail): MoveRow | null {
  const human = detail.game.humanColor;
  for (let i = detail.moves.length - 1; i >= 0; i--) {
    const m = detail.moves[i]!;
    if (!isBoardVertex(m.vertex)) continue;
    if (detail.game.mode === 'hotseat' || human === null || m.color === human) return m;
  }
  return null;
}
